import {
  useDynamicContext,
  useUserUpdateRequest,
} from '@dynamic-labs/sdk-react-core';
import { isSolanaWallet } from '@dynamic-labs/solana';
import { Rpc } from '@lightprotocol/stateless.js';
import { useWallet } from '@solana/wallet-adapter-react';
import { PublicKey } from '@solana/web3.js';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { Metadata } from '@repo/types-client';

import {
  compressedBalanceAtom,
  errorAtom,
  isClaimingAtom,
  isDecompressingAtom,
  successAtom,
  txIdAtom,
} from '@claim/atoms';

import { buildTransaction } from '../../utils/build-transaction';
import { createDecompressInstruction } from '../../utils/create-decompress-instruction';
import { useFetchBalance } from './use-fetch-balance';

/**
 * Custom hook to handle decompression of tokens.
 *
 * This hook builds and sends a transaction to decompress compressed tokens.
 * It handles creating the necessary instructions (including ATA creation if needed),
 * signing and sending the transaction, and updating UI state based on success or errors.
 *
 * @returns An object containing the handleDecompress function.
 */
export const useHandleDecompress = () => {
  const { signTransaction } = useWallet();
  const { fetchBalance } = useFetchBalance();
  const { updateUser } = useUserUpdateRequest();
  const { primaryWallet, user } = useDynamicContext();

  const setTxId = useSetAtom(txIdAtom);
  const setError = useSetAtom(errorAtom);
  const setSuccess = useSetAtom(successAtom);
  const setIsClaiming = useSetAtom(isClaimingAtom);
  const setIsDecompressing = useSetAtom(isDecompressingAtom);
  const [compressedBalance, setCompressedBalance] = useAtom(
    compressedBalanceAtom,
  );

  /**
   * Initiates the token decompression process.
   *
   * It validates prerequisites, creates the decompress instruction,
   * builds and sends the transaction, confirms it, updates user metadata,
   * and finally updates the UI state by refreshing balances.
   *
   * @param address - The user's wallet address.
   * @param mintAddress - The token mint address.
   * @param connection - The RPC connection instance.
   */
  const handleDecompress = async (
    address: string,
    mintAddress: PublicKey,
    connection: Rpc,
  ) => {
    // Extract the public key
    const publicKey = new PublicKey(address);
    if (!primaryWallet || !isSolanaWallet(primaryWallet)) return;
    if (!publicKey || !signTransaction || compressedBalance[address] === undefined || compressedBalance[address] === null) return;

    try {
      setIsDecompressing(true);
      setError(null);
      console.log('Starting decompression process...');

      // Create the decompress instruction and retrieve the ATA.
      const { ata, decompressIx } = await createDecompressInstruction(
        publicKey,
        mintAddress,
        connection,
        compressedBalance[address],
      );

      // Build the transaction with necessary instructions.
      const { transaction, blockhash, lastValidBlockHeight } =
        await buildTransaction(
          publicKey,
          ata,
          decompressIx,
          connection,
          mintAddress,
        );

      console.log('Transaction instructions:', transaction.instructions);

      const signer = await primaryWallet.getSigner();
      const signedTx = await signer.signAndSendTransaction(transaction as any);
      const txId = signedTx.signature;

      console.log('Transaction sent:', txId);

      await connection.confirmTransaction(
        {
          blockhash,
          lastValidBlockHeight,
          signature: txId,
        },
        'confirmed',
      );

      if ((user?.metadata as Metadata)?.solanaClaimAddress) {
        await updateUser({
          metadata: {
            ...user?.metadata,
            solanaClaimAddress: null,
          },
        });
      }

      console.log('Transaction successful:', txId);
      setIsClaiming(true);
      setSuccess(true);
      setCompressedBalance((prev) => ({
        ...prev,
        [address]: 0,
      }));
      setTxId(txId);
      await fetchBalance(address, mintAddress, connection);
    } catch (err: any) {
      if (err.message && err.message.includes('User rejected the request')) {
        setError('Rejected');
      } else {
        setError('Failed to decompress tokens');
      }

      console.error('Decompression error:', err);
    } finally {
      setIsDecompressing(false);
    }
  };

  return {
    handleDecompress,
  };
};
